
export interface FilterOption {
  key: number | string;
  value: string;
}

interface Filter {
  key: string;
  label: string;
  options: FilterOption[];
  multiple?: boolean;
}

interface FiltersSectionProps {
  filters: Filter[];
  showFilters: boolean;
  selectedFilters: Record<string, (string | number)[]>;
  handleFilterChange: (filterKey: string, value: string, remove?: boolean) => void;
  hideFilters?: boolean;
}

export function FiltersSection({
  filters,
  showFilters,
  selectedFilters,
  handleFilterChange,
}: FiltersSectionProps) {
  return (
    <div className="mb-4">
      {filters && filters.length > 0 && showFilters && (
        <div className="border rounded-lg shadow-sm bg-white">
          <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {filters.map((filter) => (
              <div key={filter.key} className="border-b sm:border-r last:border-r-0 last:border-b-0">
                <FilterGroup
                  label={filter.label}
                  options={filter.options}
                  multiple={filter.multiple || false}
                  selectedValues={selectedFilters[filter.key] || []}
                  onChange={(value) => handleFilterChange(filter.key, String(value))}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface FilterGroupProps {
  label: string;
  options: FilterOption[];
  multiple: boolean;
  selectedValues: (number | string)[];
  onChange: (value: number | string) => void;
}

export function FilterGroup({
  label,
  options,
  multiple,
  selectedValues,
  onChange,
}: FilterGroupProps) {
  return (
    <div className="p-3">
      <p className="text-xs font-bold uppercase tracking-wide text-gray-700 mb-2">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selectedValues.map(String).includes(String(option.key));
          return (
            <label
              key={option.key}
              className={`
                flex items-center space-x-2 px-3 py-1.5 rounded-lg cursor-pointer
                transition-all duration-200 ease-in-out text-sm
                ${isSelected
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 font-medium'
                  : 'bg-gray-50 text-gray-600 border border-gray-100 hover:bg-gray-100'}
              `}
            >
              <input
                type={multiple ? "checkbox" : "radio"}
                name={label}
                value={String(option.key)}
                checked={isSelected}
                onChange={() => onChange(String(option.key))}
                className={`
                  w-3 h-3
                  ${multiple
                    ? 'rounded text-blue-600 focus:ring-blue-500'
                    : 'rounded-full text-blue-600 focus:ring-blue-500'}
                `}
              />
              <span className="text-sm">{option.value}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
